import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Users, ScanLine, SplitSquareHorizontal, Receipt, Undo2, Lock, ArrowLeft, Moon, Sun } from "lucide-react";
import { toast } from "sonner";
import { Wordmark } from "@/components/Header";
import { useTheme } from "@/lib/theme";
import PeopleSection from "@/components/PeopleSection";
import ReceiptSection from "@/components/ReceiptSection";
import AssignSection from "@/components/AssignSection";
import SummarySection from "@/components/SummarySection";
import { getBill, patchBill, getSummary, uploadReceipt, createShare } from "@/lib/api";
import { saveRecent } from "@/pages/Landing";
import { itemUnits, isFullyAssigned, itemPortions, totalAssigned } from "@/lib/format";

const PALETTE = ["#EF4444", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316"];
const STEPS = [
  { key: "receipt", label: "Receipt", icon: ScanLine },
  { key: "people", label: "People", icon: Users },
  { key: "assign", label: "Assign", icon: SplitSquareHorizontal },
  { key: "summary", label: "Summary", icon: Receipt },
];

export default function BillWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const [bill, setBill] = useState(null);
  const [summary, setSummary] = useState(null);
  const [step, setStep] = useState("receipt");
  const [scanning, setScanning] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef(null);
  const undoStack = useRef([]);

  const refreshSummary = useCallback(async () => {
    try {
      setSummary(await getSummary(id));
    } catch {}
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const b = await getBill(id);
        setBill(b);
        saveRecent(b);
        if (b.share) setShareInfo({ token: b.share.token, url: `${window.location.origin}/s/${b.share.token}`, expires_at: b.share.expires_at });
        setSummary(await getSummary(id));
      } catch {
        toast.error("Bill not found.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Keep the guided flow consistent: if the current step becomes locked
  // (e.g. all people/items were removed), fall back to the furthest valid step.
  useEffect(() => {
    if (!bill) return;
    const hp = bill.participants.length > 0;
    const hi = bill.items.length > 0;
    const aa = hi && bill.items.every((it) => isFullyAssigned(it));
    const ok = { receipt: true, people: hi, assign: hp && hi, summary: hp && hi && aa };
    if (!ok[step]) setStep(hi ? (hp ? "assign" : "people") : "receipt");
  }, [bill, step]);

  const persist = useCallback(
    (next) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        try {
          const payload = {
            title: next.title,
            currency: next.currency,
            participants: next.participants,
            items: next.items,
            service_pct: next.service_pct,
            tax_pct: next.tax_pct,
            service_amount: next.service_amount ?? null,
            tax_amount: next.tax_amount ?? null,
            discount_amount: next.discount_amount,
            rounding_adjustment: next.rounding_adjustment || 0,
            printed_total: next.printed_total,
            payments: next.payments || {},
            payment: next.payment || null,
          };
          await patchBill(id, payload);
          await refreshSummary();
        } catch {
          toast.error("Could not save changes.");
        }
      }, 450);
    },
    [id, refreshSummary]
  );

  const mutate = useCallback(
    (producer, { undoLabel } = {}) => {
      setBill((prev) => {
        if (!prev) return prev;
        if (undoLabel) {
          undoStack.current.push(JSON.parse(JSON.stringify(prev)));
        }
        const next = producer(JSON.parse(JSON.stringify(prev)));
        persist(next);
        return next;
      });
      if (undoLabel) {
        toast(undoLabel, {
          action: {
            label: "Undo",
            onClick: () => {
              const snap = undoStack.current.pop();
              if (snap) {
                setBill(snap);
                persist(snap);
              }
            },
          },
          icon: <Undo2 size={16} />,
        });
      }
    },
    [persist]
  );

  // ---- handlers
  const addPerson = (name) => {
    mutate((b) => {
      const existing = b.participants.filter((p) => p.name.replace(/ \d+$/, "") === name).length;
      const finalName = existing ? `${name} ${existing + 1}` : name;
      const color = PALETTE[b.participants.length % PALETTE.length];
      b.participants.push({ id: crypto.randomUUID(), name: finalName, color });
      return b;
    });
  };
  const quickAdd = (n) => {
    mutate((b) => {
      for (let i = 0; i < n; i++) {
        const idx = b.participants.length;
        b.participants.push({ id: crypto.randomUUID(), name: `Person ${idx + 1}`, color: PALETTE[idx % PALETTE.length] });
      }
      return b;
    });
  };
  const removePerson = (pid) => {
    mutate(
      (b) => {
        b.participants = b.participants.filter((p) => p.id !== pid);
        b.items.forEach((it) => {
          it.assigned_to = (it.assigned_to || []).filter((a) => a !== pid);
          if (it.portions) delete it.portions[pid];
        });
        if (b.payments) delete b.payments[pid];
        return b;
      },
      { undoLabel: "Person removed" }
    );
  };
  const setPayment = (patch) => mutate((b) => ({ ...b, payment: { ...(b.payment || {}), ...patch } }));
  const addItem = () => mutate((b) => {    b.items.push({ id: crypto.randomUUID(), name: "", qty: 1, unit_price: 0, assigned_to: [] });
    return b;
  });
  const itemChange = (iid, patch) => mutate((b) => {
    b.items = b.items.map((it) => (it.id === iid ? { ...it, ...patch } : it));
    return b;
  });
  const removeItem = (iid) => mutate((b) => {
    b.items = b.items.filter((it) => it.id !== iid);
    if (b.items.length === 0) {
      // No items left: clear the previous receipt's charges so a fresh entry starts clean.
      b.service_pct = 0;
      b.tax_pct = 0;
      b.service_amount = null;
      b.tax_amount = null;
      b.discount_amount = 0;
      b.rounding_adjustment = 0;
      b.printed_total = null;
    }
    return b;
  }, { undoLabel: "Item removed" });
  const toggleAssign = (iid, pid) => {
    const cur = bill?.items?.find((x) => x.id === iid);
    const qty = Number(cur?.qty) || 0;
    if (cur && (itemPortions(cur)[pid] || 0) + 1 > qty + 1e-6) {
      toast.message("Already the whole item", { description: "This person can't take more than what was ordered." });
      return;
    }
    mutate((b) => {
      b.items = b.items.map((it) => {
        if (it.id !== iid) return it;
        const portions = { ...itemPortions(it) };
        // cap each PERSON at qty; multiple people may share the same item
        if ((portions[pid] || 0) + 1 > (Number(it.qty) || 0) + 1e-6) return it;
        portions[pid] = (portions[pid] || 0) + 1;
        return { ...it, portions, assigned_to: [] };
      });
      return b;
    });
  };
  const removeUnit = (iid, pid) => mutate((b) => {
    b.items = b.items.map((it) => {
      if (it.id !== iid) return it;
      const portions = { ...itemPortions(it) };
      if (portions[pid]) {
        const nv = portions[pid] - 1;
        if (nv > 1e-6) portions[pid] = nv;
        else delete portions[pid];
      }
      return { ...it, portions, assigned_to: [] };
    });
    return b;
  });
  const splitEvenly = (iid) => mutate((b) => {
    const ids = b.participants.map((p) => p.id);
    b.items = b.items.map((it) => {
      if (it.id !== iid) return it;
      const qty = Number(it.qty) || 0;
      const n = ids.length;
      if (n === 0 || qty <= 0) return { ...it, portions: {}, assigned_to: [] };
      const each = qty / n; // e.g. 3 nasi / 2 people = 1.5 each
      const portions = {};
      ids.forEach((pid) => (portions[pid] = each));
      return { ...it, portions, assigned_to: [] };
    });
    return b;
  });
  const onCharges = (patch) => mutate((b) => ({ ...b, ...patch }));
  const togglePaid = (pid, paid) => mutate((b) => {
    b.payments = { ...(b.payments || {}), [pid]: paid };
    return b;
  });

  const onUpload = async (file) => {
    setScanning(true);
    try {
      const res = await uploadReceipt(id, file);
      setBill(res.bill);
      await refreshSummary();
      const n = res.parsed?.items?.length || 0;
      const named = res.parsed?.merchant;
      toast.success(n ? `Found ${n} item${n > 1 ? "s" : ""}${named ? ` at ${named}` : ""}. Review below.` : "Scan done, add items manually if needed.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Receipt scan failed. Try a clearer photo.";
      toast.error(typeof msg === "string" ? msg : "Receipt scan failed.");
    } finally {
      setScanning(false);
    }
  };

  const onShare = async () => {
    try {
      const s = await createShare(id, 7);
      const info = { token: s.token, url: `${window.location.origin}/s/${s.token}`, expires_at: s.expires_at };
      setShareInfo(info);
      return info;
    } catch {
      toast.error("Could not create share link.");
      return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--gz-body)] text-[var(--gz-muted)]">Loading…</div>
    );
  }
  if (!bill) {
    return <div className="min-h-screen grid place-items-center bg-[var(--gz-body)] text-[var(--gz-muted)]">Bill not found.</div>;
  }

  const charges = {
    service_pct: bill.service_pct,
    tax_pct: bill.tax_pct,
    service_amount: bill.service_amount,
    tax_amount: bill.tax_amount,
    discount_amount: bill.discount_amount,
    rounding_adjustment: bill.rounding_adjustment,
    printed_total: bill.printed_total,
  };

  const hasPeople = bill.participants.length > 0;
  const hasItems = bill.items.length > 0;
  const allAssigned = hasItems && bill.items.every((it) => isFullyAssigned(it));
  const unlocked = {
    receipt: true,
    people: hasItems,
    assign: hasPeople && hasItems,
    summary: hasPeople && hasItems && allAssigned,
  };
  const go = (key) => {
    if (unlocked[key]) {
      setStep(key);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (!hasItems) {
      toast.message("Add the receipt first", { description: "Scan a receipt or add an item to continue." });
    } else if (!hasPeople) {
      toast.message("Add people first", { description: "Add at least one person to continue." });
    } else if (key === "summary" && !allAssigned) {
      toast.message("Assign every item first", { description: "Each dish needs at least one person before the breakdown." });
    } else {
      toast.message("Finish the earlier steps first");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--gz-body)] pb-28">
      {/* sticky compact top bar */}
      <div className="sticky top-0 z-40 bg-[var(--gz-header)] text-white gz-safe-top">
        <div className="max-w-md mx-auto px-4 h-12 flex items-center gap-1.5">
          <button
            data-testid="workspace-home-btn"
            onClick={() => navigate("/")}
            aria-label="Home"
            className="grid place-items-center h-9 w-9 -ml-1.5 rounded-full hover:bg-white/10 active:scale-90 transition"
          >
            <ArrowLeft size={18} />
          </button>
          <Wordmark className="text-[15px]" />
          <div className="flex-1" />
          <button
            data-testid="theme-toggle-btn"
            onClick={toggle}
            aria-label="Toggle dark mode"
            className="grid place-items-center h-9 w-9 rounded-full hover:bg-white/10 active:scale-90 transition"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </div>

      {/* blue title strip */}
      <div className="bg-[var(--gz-header)] text-white">
        <div className="max-w-md mx-auto px-4 pb-4">
          <input
            data-testid="bill-title-input"
            value={bill.title}
            onChange={(e) => onCharges({ title: e.target.value })}
            className="w-full bg-transparent text-white text-base font-semibold tracking-tight outline-none px-1"
            aria-label="Bill title"
          />
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pt-4">
        <div className="rounded-2xl bg-[var(--gz-card)] gz-paper-shadow p-4 min-h-[60vh]">
          {step === "people" && <PeopleSection participants={bill.participants} onAdd={addPerson} onRemove={removePerson} onContinue={() => go("assign")} />}
          {step === "receipt" && (
            <ReceiptSection
              items={bill.items}
              charges={charges}
              currency={bill.currency || "IDR"}
              scanning={scanning}
              onUpload={onUpload}
              onItemChange={itemChange}
              onAddItem={addItem}
              onRemoveItem={removeItem}
              onCharges={onCharges}
              summary={summary}
              onContinue={() => go("people")}
            />
          )}
          {step === "assign" && (
            <AssignSection participants={bill.participants} items={bill.items} currency={bill.currency || "IDR"} onAddUnit={toggleAssign} onRemoveUnit={removeUnit} onSplitEvenly={splitEvenly} summary={summary} onContinue={() => go("summary")} canContinue={allAssigned} />
          )}
          {step === "summary" && (
            <SummarySection bill={bill} summary={summary} onTogglePaid={togglePaid} onShare={onShare} shareInfo={shareInfo} onPayment={setPayment} />
          )}
        </div>
      </div>

      {/* bottom step nav */}
      <div className="fixed bottom-0 inset-x-0 z-20">
        <div className="max-w-md mx-auto px-4 gz-safe-bottom">
          <div className="rounded-2xl bg-[var(--gz-card)] gz-paper-shadow border border-[var(--gz-line)] grid grid-cols-4 p-1.5">
            {STEPS.map((s, i) => {
              const active = step === s.key;
              const isLocked = !unlocked[s.key];
              return (
                <button
                  key={s.key}
                  data-testid={`step-${s.key}`}
                  onClick={() => go(s.key)}
                  aria-disabled={isLocked}
                  className={`relative flex flex-col items-center gap-1 py-2 rounded-xl transition-colors ${
                    active ? "bg-[var(--gz-primary)] text-white" : isLocked ? "text-[var(--gz-muted)] opacity-40" : "text-[var(--gz-muted)]"
                  }`}
                >
                  <span className="relative">
                    <s.icon size={19} />
                    {isLocked && !active && (
                      <Lock size={10} className="absolute -right-1.5 -top-1 bg-[var(--gz-card)] rounded-full" />
                    )}
                  </span>
                  <span className="text-[11px] font-medium flex items-center gap-0.5">
                    <span className="opacity-70">{i + 1}.</span> {s.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
